// src/components/global/GlobalBreadcrumbs.jsx
import React from 'react';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { sidebarItems } from '../../constants/sidebarItems';
import { useTranslation } from 'react-i18next';

const segmentLabels = {
    create: { en: 'Create', ar: 'إنشاء' },
    edit: { en: 'Edit', ar: 'تعديل' },
    view: { en: 'View', ar: 'عرض' },
    archive: { en: 'Archive', ar: 'الأرشيف' },
    'delete-all': { en: 'Delete All', ar: 'حذف الكل' },
    'job-orders': { en: 'Job Orders & Vouchers', ar: 'أوامر التشغيل والفاوتشر' },

};

const toTitle = (segment, lng) => {
    const clean = segment.split('?')[0];

    // أولوية لاستخدام الترجمة المعرّفة
    if (segmentLabels[clean]) return segmentLabels[clean][lng];

    // fallback: users-profile => Users Profile
    return clean
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
};

const GlobalBreadcrumbs = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { i18n } = useTranslation();  // يربط الكمبوننت بأحداث تغيير اللغة
    let lng = i18n.language || 'en';

    // عنصر الداشبورد من السايدبار
    const dashboardItem = sidebarItems.find(item => item.key === 'dashboard');

    // مثال: /users/create => ['users','create']
    const pathParts = location.pathname.split('/').filter(Boolean);

    // 1) الداشبورد ثابت دائمًا أول عنصر
    const crumbs = [
        {
            label: dashboardItem ? dashboardItem.label[lng] : 'Dashboard',
            path: '/',
            clickable: true,
        },
    ];

    // لو نحن في /
    if (pathParts.length === 0) {
        return (
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Typography color="text.primary" fontWeight={600}>
                    {crumbs[0].label}
                </Typography>
            </Breadcrumbs>
        );
    }

    // 2) استنتاج العنصر الأساسي من أول جزء وربطه بالـ sidebarItems
    const baseSegment = `/${pathParts[0]}`; // مثل /users, /products, /countries
    const baseItem =
        sidebarItems.find(item => item.path === baseSegment) || null;

    if (baseItem) {
        crumbs.push({
            label: baseItem.label[lng], // Users / Products / ...
            path: baseItem.path,
            clickable: true,
        });
    }

    // 3) باقي الأجزاء: create, edit, archive, delete-all, id...
    // أمثلة:
    // /users/create          => Dashboard > Users > Create
    // /users/archive         => Dashboard > Users > Archive
    // /users/delete-all      => Dashboard > Users > Delete All
    // /users/123/edit        => Dashboard > Users > 123 > Edit
    // 3) باقي الأجزاء (create, edit, archive, delete-all, id...)
    let currentPath = baseSegment;
    for (let i = 1; i < pathParts.length; i++) {
        const seg = pathParts[i];
        const isLast = i === pathParts.length - 1;

        // لو هذا الجزء رقم (id) واللي بعده edit أو create تخطّه في الـ UI
        const isNumericId = /^\d+$/.test(seg);
        const nextSeg = pathParts[i + 1];
        const isEditOrCreateNext = nextSeg === 'edit' || nextSeg === 'create';

        if (isNumericId && isEditOrCreateNext) {
            currentPath += `/${seg}`; // نحتفظ به في المسار الداخلي فقط
            continue;                 // لا نضيفه كـ crumb ظاهر
        }

        currentPath += `/${seg}`;

        crumbs.push({
            label: toTitle(seg, lng),
            path: currentPath,
            clickable: !isLast,
        });
    }


    return (
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;

                if (!crumb.clickable || isLast) {
                    return (
                        <Typography
                            key={index}
                            color="text.primary"
                            fontWeight={600}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {crumb.label}
                        </Typography>
                    );
                }

                return (
                    <Link
                        key={index}
                        underline="hover"
                        color="inherit"
                        sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
                        onClick={() => navigate(crumb.path)}
                    >
                        {crumb.label}
                    </Link>
                );
            })}
        </Breadcrumbs>
    );
};

export default GlobalBreadcrumbs;
